<template>
  <div class="container-fluid px-0">
    <div class="row mb-4 g-2" :dir="locale === 'ar' ? 'rtl' : 'ltr'">
      <div class="col-6 col-md-2 col-lg-2">
        <select 
          class="form-select form-select-sm" 
          :class="locale === 'ar' ? 'form-select-rtl' : 'form-select-ltr'"
          v-model="localFilters.role_id" 
          @change="applyFilters"
        >
          <option value="">{{ $t('users.filters.allRoles') }}</option>
      <option v-for="role in translatedRoles" :value="role.id" :key="role.id">
  {{ role.translated_name }}
</option>
        </select>
      </div>
      
      <div class="col-6 col-md-2 col-lg-2">
        <select 
          class="form-select form-select-sm" 
          :class="locale === 'ar' ? 'form-select-rtl' : 'form-select-ltr'"
          v-model="localFilters.gender" 
          @change="applyFilters"
        >
          <option value="">{{ $t('users.filters.allGenders') }}</option>
          <option value="male">{{ $t('users.filters.male') }}</option>
          <option value="female">{{ $t('users.filters.female') }}</option>
        </select>
      </div>
      
      <div class="col-md-1 d-none d-md-block">
        <button class="btn btn-outline-secondary btn-sm w-100" @click="resetFilters">
          <i class="fas fa-filter-circle-xmark"></i>
        </button>
      </div>
      
      <div class="col-12 col-md-4 col-lg-4 position-relative">
        <input
          type="text"
          class="form-control form-control-sm"
          :class="locale === 'ar' ? 'ps-5 text-end' : 'pe-5 text-start'"
          :placeholder="$t('users.filters.searchPlaceholder')"
          :value="localFilters.search"
          @input="handleSearchInput"
          :dir="locale === 'ar' ? 'rtl' : 'ltr'"
        />
        <span class="position-absolute top-50 translate-middle-y" :class="locale === 'ar' ? 'start-0 ps-3' : 'end-0 pe-3'">
          <i class="fas fa-search"></i>
        </span>
      </div>
      
      <div class="col-12 d-md-none">
        <button class="btn btn-outline-secondary btn-sm w-100" @click="resetFilters">
          <i class="fas fa-filter-circle-xmark"></i> {{ $t('users.filters.reset') }}
        </button>
      </div>
      
      <div class="col-12 col-md-3 col-lg-3">
        <button class="btn btn-secondary btn-sm w-100" @click="navigateToAddUser">
          <i class="fas fa-plus"></i>
          {{ $t('users.actions.addNew') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, defineProps, defineEmits, onUnmounted, computed } from 'vue';
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n';

const { locale } = useI18n();

const translatedRoles = computed(() => {
  return props.roles.map(role => {
    try {
      const nameData = typeof role.name === 'string' ? JSON.parse(role.name) : role.name;
      return {
        ...role,
        translated_name: nameData[locale.value] || nameData.en || role.name
      };
    } catch (e) {
      return {
        ...role,
        translated_name: role.name
      };
    }
  });
});
const props = defineProps({
  filters: {
    type: Object,
    required: true,
    default: () => ({
      role_id: '',
      gender: '',
      search: ''
    })
  },
  roles: {
    type: Array,
    default: () => [],
    validator: value => {
      return value.every(role => 'id' in role && 'name' in role)
    }
  }
});

const router = useRouter();
const emit = defineEmits(['update:filters', 'apply-filters', 'reset-filters', 'add', 'search']);

const localFilters = ref(JSON.parse(JSON.stringify(props.filters)));
let debounceTimeout = null;

const debounce = (fn, delay) => {
  return (...args) => {
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }
    
    debounceTimeout = setTimeout(() => {
      fn(...args);
      debounceTimeout = null;
    }, delay);
  };
};

onUnmounted(() => {
  if (debounceTimeout) {
    clearTimeout(debounceTimeout);
  }
});

const debouncedSearch = debounce((searchValue) => {
  localFilters.value.search = searchValue;
  applyFilters();
}, 500);

const handleSearchInput = (event) => {
  emit('search', event.target.value);
  debouncedSearch(event.target.value);
};

const applyFilters = () => {
  const filtersToEmit = {
    ...localFilters.value,
    role_id: localFilters.value.role_id ? Number(localFilters.value.role_id) : '',
    gender: localFilters.value.gender || '' // Ensure gender is properly passed
  };
  
  emit('update:filters', filtersToEmit);
  emit('apply-filters');
};
const resetFilters = () => {
  if (debounceTimeout) {
    clearTimeout(debounceTimeout);
    debounceTimeout = null;
  }
  
  localFilters.value = {
    role_id: '',
    gender: '',
    search: ''
  };
  emit('update:filters', localFilters.value);
  emit('reset-filters');
};

const navigateToAddUser = () => {
  router.push('/users/add');
};

watch(() => props.filters, (newVal) => {
  localFilters.value = JSON.parse(JSON.stringify(newVal));
}, { deep: true });
</script>